import type { SelectProps as AriaSelectProps, ValidationResult } from "react-aria-components";
import { Button, ListBox, Select, SelectValue } from "react-aria-components";
import type { ComponentProps } from "react";
import React from "react";
import Skeleton from "react-loading-skeleton";
import { tv } from "tailwind-variants";

import type { BaseItem } from "@/core/types";
import ChevronIcon from "@/assets/images/icons2/icon--chevron.svg?component";
import { cn } from "@/lib/helpers/cn";

import { Checkbox2 } from "../atoms/Checkbox";
import { FieldError2 } from "../atoms/FieldError";
import { Label2 } from "../atoms/Label";
import { ListBoxItem2 } from "../atoms/ListBoxItem";
import { Popover2 } from "../atoms/Popover";
import { Text2 } from "../atoms/Text";

type MultiSelectProps<T extends BaseItem> = {
  className?: string;
  description?: string;
  errorMessage?: ((validation: ValidationResult) => string) | string;
  hasStrongLabel?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  label: string;
  options: T[];
  placeholder?: string;
  renderItem?: (item: T) => React.ReactNode;
} & Omit<AriaSelectProps<T, "multiple">, "children" | "selectionMode">;

export type { MultiSelectProps };

export function MultiSelect2<T extends BaseItem = BaseItem>({
  className,
  description,
  errorMessage,
  hasStrongLabel,
  isDisabled,
  isInvalid,
  isRequired,
  label,
  options,
  placeholder,
  renderItem = (item: T) => item.name,
  ...props
}: MultiSelectProps<T>) {
  const { button, icon, listBox, popover, select, selectValue } = multiSelectStyles();

  return (
    <Select
      className={cn(select(), className)}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isRequired={isRequired}
      selectionMode="multiple"
      {...props}
    >
      <Label2
        className={hasStrongLabel ? "tw:font-bold" : ""}
        isInvalid={isInvalid}
        isRequired={isRequired}
        label={label}
      />

      <Button className={cn(button(), "atom-focus group")}>
        <ChevronIcon className={icon()} />

        <SelectValue<T> className={selectValue()}>
          {({ selectedItems }) =>
            selectedItems.length > 0
              ? selectedItems.map((item) => item?.name).join(", ")
              : (placeholder ?? "Select...")
          }
        </SelectValue>
      </Button>

      {description && !isInvalid && <Text2 slot="description" text={description} />}

      <FieldError2>{errorMessage}</FieldError2>

      <Popover2 className={popover()}>
        <ListBox className={listBox()} items={options as T[]}>
          {(item) => (
            <ListBoxItem2 id={item.id} textValue={item.name}>
              {({ isSelected }) => (
                <div className="tw:flex tw:w-full tw:items-center tw:gap-2">
                  <Checkbox2 isSelected={isSelected} slot="selection" />
                  {renderItem(item)}
                </div>
              )}
            </ListBoxItem2>
          )}
        </ListBox>
      </Popover2>
    </Select>
  );
}

export function MultiSelectSkeleton(props: ComponentProps<typeof Skeleton>) {
  const { height = 44, width = "100%" } = props;

  return (
    <div className="tw:flex tw:flex-col tw:gap-y-1.5 tw:leading-none">
      <Skeleton height={16} width={120} />
      <Skeleton height={height} width={width} {...props} />
    </div>
  );
}

const multiSelectStyles = tv({
  slots: {
    button: cn(
      "tw:flex tw:h-11 tw:flex-row-reverse tw:items-center tw:justify-between tw:p-2.5",
      "tw:gap-x-2 tw:text-sm",
      "tw:rounded-sm! tw:border tw:border-solid tw:border-stroke-tertiary tw:bg-fill-white",
      "tw:cursor-pointer tw:rounded",
      "tw:transition-all tw:duration-150",
      "tw:hover:border-stroke-secondary",
      "tw:data-disabled:border-stroke-tertiary tw:data-disabled:bg-fill-senary",
      "tw:disabled:cursor-not-allowed tw:disabled:text-text-quinary",
      "tw:group-data-invalid:border-stroke-error-primary",
    ),
    icon: cn(
      "tw:h-6 tw:w-6 tw:text-fill-secondary",
      "tw:transition-transform tw:duration-200",
      "tw:group-data-open:rotate-180",
    ),
    listBox: cn("tw:max-h-75 tw:overflow-y-auto", "tw:outline-none", "tw:py-1"),
    popover: "tw:w-(--trigger-width)",
    select: "tw:group tw:flex tw:flex-col tw:gap-1.5 tw:w-full",
    selectValue: cn("tw:text-left tw:text-sm", "tw:data-placeholder:text-text-quinary"),
  },
});
